import { useEffect, useMemo, useState } from 'react';
import { FiEdit3, FiSave, FiTrash2, FiUsers, FiX } from 'react-icons/fi';
import { TableHeader, TablePerPage } from '../../../../../_CORE/APP/Templates/_UI/Table/_index';
import { FieldSelectSearch } from '../../../../../_CORE/APP/Templates/_UI/Form/_Fields/FieldSelectSearch';
import { FooterPagination } from '../../../../../_CORE/APP/Templates/Layouts/Section/Table/_Plugins/FooterPagination';
import { EmailSectionLayout } from '../_UI/EmailSectionLayout';
import { EmailFieldHint, EmailFieldLabel } from '../_UI/EmailFieldGuide';
import { useEmailAdmin } from '../_Hooks/useEmailAdmin';
import { useEmailEditorNavigation } from '../_Hooks/useEmailEditorNavigation';
import { EMAIL_ADMIN_CONSTANTS as C } from '../_MODULES/_API';
import { confirm } from '../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost';

const EMPTY = { name: '', description: '', color: '#616161', active: true, contact_ids: [] };
const norm = (value = '') => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export const EmailListsView = () => {
    const api = useEmailAdmin();
    const { editorRef, goToEditor } = useEmailEditorNavigation();
    const [items, setItems] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState(null);
    const [order, setOrder] = useState('updated-desc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(12);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [values, setValues] = useState(EMPTY);
    const [message, setMessage] = useState('');

    const load = async () => {
        try {
            const [lists, contactRows] = await Promise.all([api.get(C.LISTS), api.get(C.CONTACTS)]);
            setItems(lists || []); setContacts(contactRows || []); setMessage('');
        } catch (err) { setMessage(err.message); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const contactOptions = useMemo(() => contacts.map((contact) => ({
        value: contact.id,
        label: `${[contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.email} — ${contact.email}${contact.company ? ` · ${contact.company}` : ''}`,
    })), [contacts]);

    const selectedContacts = useMemo(() => {
        const ids = new Set((values.contact_ids || []).map((id) => Number(id)));
        return contactOptions.filter((option) => ids.has(Number(option.value)));
    }, [contactOptions, values.contact_ids]);

    const startCreate = () => { setEditing(null); setValues(EMPTY); setFormOpen(true); setMessage(''); goToEditor(); };
    const startEdit = async (item) => {
        try {
            const full = await api.get(C.LIST_GET, { id: item.id });
            setEditing(full); setValues({ ...EMPTY, ...full, contact_ids: full?.contact_ids || [] }); setFormOpen(true); setMessage('');
            goToEditor();
        } catch (err) { setMessage(err.message); }
    };
    const submit = async (event) => {
        event.preventDefault();
        try {
            await api.securePost(C.LIST_SAVE, { ...values, id: editing?.id || undefined, active: values.active ? 1 : 0 });
            setFormOpen(false); setEditing(null); await load();
        } catch (err) { setMessage(err.message); }
    };
    const remove = async (item) => {
        if (!(await confirm.danger(`Eliminar la lista "${item.name}"?`, 'Los contactos no se eliminan, solo se quita la segmentacion.'))) return;
        try { await api.securePost(C.LIST_REMOVE, { id: item.id }); await load(); }
        catch (err) { setMessage(err.message); }
    };

    const filtered = useMemo(() => {
        const term = norm(search);
        const selectedActive = activeFilter?.value ?? activeFilter;
        const rows = items.filter((item) => {
            if (term && !norm(`${item.name} ${item.description}`).includes(term)) return false;
            if (selectedActive !== null && selectedActive !== undefined && selectedActive !== '' && String(item.active ? 1 : 0) !== String(selectedActive)) return false;
            return true;
        });
        return [...rows].sort((a, b) => {
            if (order === 'name-asc') return String(a.name).localeCompare(String(b.name));
            if (order === 'contacts-desc') return Number(b.contacts_total || 0) - Number(a.contacts_total || 0);
            return new Date(b.updated_at) - new Date(a.updated_at);
        });
    }, [items, search, activeFilter, order]);

    useEffect(() => { setPage(1); }, [search, activeFilter, order, perPage]);
    const totalPages = perPage === -1 ? 1 : Math.max(1, Math.ceil(filtered.length / perPage));
    const safePage = Math.min(page, totalPages);
    const visible = perPage === -1 ? filtered : filtered.slice((safePage - 1) * perPage, safePage * perPage);
    const from = filtered.length ? (perPage === -1 ? 1 : ((safePage - 1) * perPage) + 1) : 0;
    const to = filtered.length ? (perPage === -1 ? filtered.length : Math.min(safePage * perPage, filtered.length)) : 0;

    return (
        <EmailSectionLayout title="Listas" description="Segmenta tus contactos para dirigir cada campaña a grupos concretos sin duplicar destinatarios.">
            <section className="PBADMIN__panel PBMAIL__panel">
                <div className="PBADMIN__collection_tools PBMAIL__collection_tools">
                    <TableHeader
                        pill="LISTAS"
                        title="Segmentar"
                        titleAccent="contactos"
                        newLabel="Nueva lista"
                        onNew={startCreate}
                        search={search}
                        onSearch={setSearch}
                        searchPlaceholder="Buscar lista o descripción…"
                        orderOptions={[{ value: 'updated-desc', label: 'Actualizadas recientes' }, { value: 'name-asc', label: 'Nombre A → Z' }, { value: 'contacts-desc', label: 'Más contactos' }]}
                        order={order}
                        onOrder={setOrder}
                        filters={[{ key: 'active', label: 'Estado', type: 'select', value: activeFilter, onChange: setActiveFilter, placeholder: 'Todos', options: [{ value: '1', label: 'Activa' }, { value: '0', label: 'Inactiva' }] }]}
                        onClearFilters={() => { setSearch(''); setActiveFilter(null); }}
                        stats={<div className="PBADMIN__filter_summary"><span><strong>{filtered.length}</strong> lista{filtered.length === 1 ? '' : 's'}</span><span>{items.reduce((sum, item) => sum + Number(item.contacts_total || 0), 0)} asignaciones</span></div>}
                    />
                </div>

                {message && <div className="PBADMIN__alert">{message}</div>}

                {formOpen && (
                    <form ref={editorRef} className="PBADMIN__editor PBADMIN__form_grid PBMAIL__form PBMAIL__editor" onSubmit={submit}>
                        <div className="PBADMIN__editor_head"><strong>{editing ? 'Editar lista' : 'Nueva lista'}</strong><button type="button" onClick={() => setFormOpen(false)}><FiX /></button></div>
                        <label className="PBADMIN__field">
                            <EmailFieldLabel status="required">Nombre</EmailFieldLabel>
                            <input required placeholder="Ej: RRHH Argentina" value={values.name || ''} onChange={(e) => setValues((p) => ({ ...p, name: e.target.value }))} />
                            <EmailFieldHint>Usa un nombre que te recuerde claramente a quien agrupa.</EmailFieldHint>
                        </label>
                        <label className="PBADMIN__field">
                            <EmailFieldLabel status="optional">Color de referencia</EmailFieldLabel>
                            <input value={values.color || '#616161'} onChange={(e) => setValues((p) => ({ ...p, color: e.target.value }))} placeholder="#616161" />
                            <EmailFieldHint>Solo es visual; no afecta los envios.</EmailFieldHint>
                        </label>
                        <label className="PBADMIN__field PBADMIN__field--2">
                            <EmailFieldLabel status="recommended">Descripcion</EmailFieldLabel>
                            <textarea rows="3" placeholder="Ej: Recruiters y empresas de software de Argentina para busqueda laboral." value={values.description || ''} onChange={(e) => setValues((p) => ({ ...p, description: e.target.value }))} />
                            <EmailFieldHint>Te ayuda a recordar para que creaste la lista.</EmailFieldHint>
                        </label>
                        <div className="PBADMIN__field PBADMIN__field--2 PBMAIL__select_field">
                            <FieldSelectSearch
                                id="email-list-contacts"
                                label="Contactos de la lista"
                                hint="Opcional: podes agregarlos ahora, despues desde Contactos o al importar un archivo."
                                value={selectedContacts}
                                onChange={(selected) => setValues((p) => ({ ...p, contact_ids: (selected || []).map((option) => option.value) }))}
                                options={contactOptions}
                                placeholder="Ej: buscar por nombre o email..."
                                isMulti
                            />
                        </div>
                        <div className="PBMAIL__check_group PBADMIN__field--2">
                            <label className="PBADMIN__check"><input type="checkbox" checked={!!values.active} onChange={(e) => setValues((p) => ({ ...p, active: e.target.checked }))} /><span>Lista activa</span></label>
                            <EmailFieldHint>Dejala activa para poder seleccionarla como objetivo de una campana.</EmailFieldHint>
                        </div>
                        <div className="PBADMIN__form_footer"><button className="PBADMIN__primary" type="submit" disabled={api.isLoading}><FiSave /> Guardar lista</button><button type="button" className="PBADMIN__secondary" onClick={() => setFormOpen(false)}>Cancelar</button></div>
                    </form>
                )}

                <div className="PBMAIL__list_grid">
                    {visible.length === 0 && !api.isLoading && <div className="PBADMIN__empty">No hay listas para mostrar.</div>}
                    {visible.map((item) => (
                        <article className="PBMAIL__list_card" key={item.id}>
                            <div className="PBMAIL__list_marker" style={{ background: item.color || '#616161' }} />
                            <div className="PBMAIL__list_body"><span className={`PBMAIL__status is-${item.active ? 'active' : 'inactive'}`}>{item.active ? 'Activa' : 'Inactiva'}</span><h3>{item.name}</h3><p>{item.description || 'Sin descripción.'}</p><div><FiUsers /><strong>{item.contacts_total || 0}</strong><span>contactos</span></div></div>
                            <div className="PBMAIL__campaign_card_actions"><button type="button" title="Editar" onClick={() => startEdit(item)}><FiEdit3 /></button><button type="button" className="is-danger" title="Eliminar" onClick={() => remove(item)}><FiTrash2 /></button></div>
                        </article>
                    ))}
                </div>

                {filtered.length > 0 && <div className="PBADMIN__collection_footer"><div className="PBADMIN__collection_count">Mostrando <strong>{from}–{to}</strong> de <strong>{filtered.length}</strong></div><TablePerPage value={perPage} onChange={setPerPage} totalItems={filtered.length} /><FooterPagination pagina={safePage} totalPaginas={totalPages} onIr={setPage} /></div>}
            </section>
        </EmailSectionLayout>
    );
};
